import React, { useState } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import api from "../../../utils/api";
import StarRating from "../../section/StarRating";

const OfficeOwnerDashboard = ({ userData, setUserData }) => {
    const [activeSubTab, setActiveSubTab] = useState("overview");

    // === Fetch Office Data ===
    const { data: officeData, isLoading: officeLoading, error: officeError } = useQuery({
        queryKey: ["office"],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");

            const res = await api.get("/offices", {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data;
        },
        staleTime: 1000 * 60 * 5,
    });

    // === Fetch Client Requests ===
    const { data: requestsData, isLoading: requestsLoading, error: requestsError } = useQuery({
        queryKey: ["office-requests"],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");

            const res = await api.get("/requests/office-requests", {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data;
        },
        staleTime: 1000 * 60,
    });

    // === Loading & Error States ===
    if (officeLoading || requestsLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="ml-3 text-gray-600">Loading dashboard...</p>
            </div>
        );
    }

    if (officeError || requestsError || !officeData) {
        return (
            <div className="text-center py-12 bg-red-50 rounded-lg">
                <p className="text-red-600 font-medium">Failed to load data. Please try again.</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    // === Extract Data ===
    const office = officeData;
    const plan = office.current_subscription?.plan;
    const lawyers = office.members
        ?.filter((m) => m.role === "lawyer" && m.is_active)
        .map((m) => ({
            id: m.user.id,
            name: m.user.name,
            email: m.user.email,
            phone: m.user.phone,
            rating: parseFloat(m.user.average_rating) || 0,
            totalReviews: m.user.total_ratings || 0,
            status: m.is_active ? "active" : "inactive",
            specialization: "General Practice", // يمكن إضافة حقل لاحقًا
            experience: "5+ years",
            languages: ["English"],
            clients: Math.floor(Math.random() * 30) + 10,
            revenue: Math.floor(Math.random() * 6000) + 2000,
        })) || [];

    const currentLimits = {
        lawyers: plan?.max_lawyers || 10,
        forms: plan?.max_forms_per_month || 50,
        clients: 100,
    };

    const subscriptionType = plan?.name || "Free Plan";

    // === Render Functions ===
    const renderOverview = () => (
        <div className="space-y-6">
            {/* Subscription Info */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
                        <p className="text-gray-600">{office.name}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {plan?.billing_interval === "monthly" ? "Monthly" : "Yearly"} Billing
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-yellow-600">{subscriptionType}</div>
                        <div className="text-sm text-gray-600">Plan</div>
                        <div className="text-xs text-gray-500">
                            ${plan?.price || "0.00"}/{plan?.billing_interval}
                        </div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                            {lawyers.length}/{currentLimits.lawyers}
                        </div>
                        <div className="text-sm text-gray-600">Lawyers</div>
                        <div className="text-xs text-gray-500">
                            {currentLimits.lawyers - lawyers.length} left
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                            —/{currentLimits.forms}
                        </div>
                        <div className="text-sm text-gray-600">Forms</div>
                        <div className="text-xs text-gray-500">Per month</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                            {requestsData?.length || 0}
                        </div>
                        <div className="text-sm text-gray-600">Active Requests</div>
                        <div className="text-xs text-gray-500">In progress</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">24/7</div>
                        <div className="text-sm text-gray-600">Support</div>
                        <div className="text-xs text-gray-500">Priority</div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderLawyers = () => (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Firm Lawyers</h3>
            </div>

            <div className="space-y-4">
                {lawyers.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No lawyers in this office yet.</p>
                ) : (
                    lawyers.map((lawyer, index) => (
                        <motion.div
                            key={lawyer.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-semibold text-sm">
                                            {lawyer.name.split(" ").map((n) => n[0]).join("").substring(0, 2)}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h4 className="font-semibold text-gray-900">{lawyer.name}</h4>
                                            <StarRating rating={lawyer.rating} size="xs" showNumber={true} />
                                        </div>
                                        <p className="text-sm text-gray-600">{lawyer.specialization}</p>
                                        <p className="text-sm text-gray-500">
                                            {lawyer.experience} • {lawyer.clients} clients • {lawyer.totalReviews} reviews
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">
                                            ${lawyer.revenue.toLocaleString()}
                                        </div>
                                        <div className="text-xs text-gray-500">Revenue</div>
                                    </div>
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${lawyer.status === "active"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-gray-100 text-gray-800"
                                            }`}
                                    >
                                        {lawyer.status}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))
                )}
            </div>
        </div>
    );

    const renderRequests = () => (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Client Requests</h3>
                <span className="text-sm text-gray-500">
                    {requestsData?.length || 0} request{requestsData?.length !== 1 ? "s" : ""}
                </span>
            </div>

            {requestsLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : requestsData?.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No client requests yet.</p>
            ) : (
                <div className="space-y-4">
                    {requestsData?.map((request, index) => (
                        <motion.div
                            key={request.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <h4 className="font-semibold text-gray-900">{request.title}</h4>
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${request.status === "pending"
                                                ? "bg-yellow-100 text-yellow-800"
                                                : request.status === "accepted"
                                                    ? "bg-blue-100 text-blue-800"
                                                    : request.status === "assigned"
                                                        ? "bg-purple-100 text-purple-800"
                                                        : "bg-green-100 text-green-800"
                                                }`}
                                        >
                                            {request.status}
                                        </span>
                                        {request.files_count > 0 && (
                                            <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                {request.files_count} file{request.files_count !== 1 ? "s" : ""}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-gray-600 mb-2">
                                        <strong>Client:</strong> {request.client_name}
                                    </p>
                                    <p className="text-sm text-gray-500 mb-2">
                                        <strong>Case Type:</strong> {request.case_type}
                                    </p>
                                    {request.lawyer_name && (
                                        <p className="text-sm text-gray-500 mb-2">
                                            <strong>Assigned Lawyer:</strong> {request.lawyer_name}
                                        </p>
                                    )}
                                    {request.assigned_to_paralegal && (
                                        <p className="text-sm text-gray-500">
                                            <strong>Paralegal:</strong> {request.assigned_to_paralegal}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-3">
                                        {new Date(request.created_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Office Owner Dashboard</h2>
                    <p className="text-gray-600 mt-1">
                        Welcome back, <strong>{office.owner?.name || "Owner"}</strong>
                    </p>
                </div>
            </div>

            {/* Sub Navigation */}
            <div className="mb-6">
                <nav className="flex space-x-8 border-b border-gray-200">
                    {[
                        { id: "overview", label: "Overview" },
                        { id: "lawyers", label: "Lawyers" },
                        { id: "requests", label: "Client Requests" },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeSubTab === tab.id
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            {activeSubTab === "overview" && renderOverview()}
            {activeSubTab === "lawyers" && renderLawyers()}
            {activeSubTab === "requests" && renderRequests()}
        </div>
    );
};

export default OfficeOwnerDashboard;